import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouteObject,
  useLocation,
  useParams,
} from 'react-router-dom';
import { loginRoutes } from 'shared/auth/LoginRoutes';
import { VerifyAccountContainer } from 'shared/auth/VerifyAccountContainer';
import { ServerErrorBoundary } from 'shared/errors/ServerErrorBoundary';
import { OnboardingWrapper } from 'shared/modules/onboarding/core/OnboardingWrapper';
import { basePath } from 'shared/utils/RouterUtils';
import { Suspense } from 'react';
import { ContentProgressIndicator } from 'shared/layout/ContentProgressIndicator';
import { lazyImport } from 'shared/routing/LazyRoute';
import { useCarrierFeatures } from 'shared/settings/CarrierSettingsAPI';
import { isFlagEnabled } from 'shared/settings/FeatureToggles';
import { CarrierOutlet } from './CarrierOutlet';
import { claimsRoutes } from './claims/ClaimsRoutes';
import { contactsRoutes } from './contacts/ContactsRoutes';
import { GoRedirectPage } from './go/GoRedirectPage';
import { DriverAccountWrapper } from './driver-account/DriverAccountWrapper';
import { DriverAccountDeletionLayout } from './drivers/driver-account-deletion/DriverAccountDeletionPage';
import { loadsRoutes } from './loads/LoadsRoutes';
import { PublicOfferPageContextProvider } from './offers/public-offers/PublicOfferPageContext';
import { profileRoutes } from './profile/ProfileRoutes';
import { reportsRoutes } from './reports/ReportsRoutes';
import { settingsRoutes } from './settings/SettingsRoutes';
import { usersPageRoutes } from './users/UsersPageRoutes';
import { tripsRoutes } from './trips/TripsRoutes';
import { vehiclesPageRoutes } from './vehicles/VehiclesPageRoutes';
import { lockAccountRoutes } from './lock-account/LockAccountRoutes';
import { dashboardRoutes } from './dashboard/DashboardRoutes';
import { homeRoutes } from './home/HomeRoutes';
import { onboardingPacketsRoutes } from './onboarding-packets/OnboardingPacketsRoutes';

const SuperCardExperimentPage =
  import.meta.env.VITE_APP_TARGET !== 'production'
    ? lazyImport(
        () => import('./super-card/SuperCardExperiment'),
        'SuperCardExperiment',
      )
    : null;

const TrialExpiredPage = lazyImport(
  () => import('shared/errors/TrialExpiredPage'),
  'TrialExpiredPage',
);
const LoadOffersPageMobile = lazyImport(
  () => import('shared/modules/load-offers/LoadOffersPageMobile'),
  'LoadOffersPageMobile',
);
const LoadSuggestionsPageMobile = lazyImport(
  () => import('shared/modules/load-suggestions/LoadSuggestionsPageMobile'),
  'LoadSuggestionsPageMobile',
);
const OnboardingQuestionaireStep1Page = lazyImport(
  () => import('shared/modules/onboarding/OnboardingPages'),
  'OnboardingQuestionaireStep1Page',
);
const OnboardingQuestionaireStep2Page = lazyImport(
  () => import('shared/modules/onboarding/OnboardingPages'),
  'OnboardingQuestionaireStep2Page',
);
const OnboardingQuestionaireStep3Page = lazyImport(
  () => import('shared/modules/onboarding/OnboardingPages'),
  'OnboardingQuestionaireStep3Page',
);
const OnboardingWelcomeStepPage = lazyImport(
  () => import('shared/modules/onboarding/OnboardingPages'),
  'OnboardingWelcomeStepPage',
);
const OnboardingUserInviteStepPage = lazyImport(
  () => import('shared/modules/onboarding/OnboardingPages'),
  'OnboardingUserInviteStepPage',
);
const DriverAccountPage = lazyImport(
  () => import('./driver-account/DriverAccountPages'),
  'DriverAccountPage',
);
const DriverAccountContainerPage = lazyImport(
  () => import('./driver-account/DriverAccountPages'),
  'DriverAccountContainerPage',
);
const DriverVerifyAccountPage = lazyImport(
  () => import('./driver-account/DriverAccountPages'),
  'DriverVerifyAccountPage',
);
const DriverDeleteAccountCompletionPage = lazyImport(
  () => import('./driver-account/DriverAccountPages'),
  'DriverDeleteAccountCompletionPage',
);
const DriverDeleteAccountConfirmationPage = lazyImport(
  () => import('./driver-account/DriverAccountPages'),
  'DriverDeleteAccountConfirmationPage',
);
const DriverDeleteAccountPage = lazyImport(
  () => import('./driver-account/DriverAccountPages'),
  'DriverDeleteAccountPage',
);
const CompletionStepPage = lazyImport(
  () => import('./driver-account/DriverAccountPages'),
  'CompletionStepPage',
);
const ConfirmationStepPage = lazyImport(
  () => import('./driver-account/DriverAccountPages'),
  'ConfirmationStepPage',
);
const MainStepPage = lazyImport(
  () => import('./driver-account/DriverAccountPages'),
  'MainStepPage',
);
const QuestionnaireStepPage = lazyImport(
  () => import('./driver-account/DriverAccountPages'),
  'QuestionnaireStepPage',
);
const DriverActivationPage = lazyImport(
  () => import('./drivers/driver-activation/DriverActivationPage'),
  'DriverActivationPage',
);
const DriverInvitationPage = lazyImport(
  () => import('./drivers/driver-invitation/DriverInvitationPage'),
  'DriverInvitationPage',
);
const OffersPage = lazyImport(
  () => import('./offers/offers/OffersPage'),
  'OffersPage',
);
const PublicOfferPage = lazyImport(
  () => import('./offers/public-offers/pages/PublicOfferPage'),
  'PublicOfferPage',
);
const SplitLoadsPage = lazyImport(
  () => import('./split-loads/SplitLoadsPage'),
  'SplitLoadsPage',
);
const TrackingContainerPage = lazyImport(
  () => import('./tracking/TrackingContainer'),
  'TrackingContainerPage',
);
const UserInvitationPage = lazyImport(
  () => import('./users/user-invitation/UserInvitationPage'),
  'UserInvitationPage',
);
const SuperCardPageDesktop = lazyImport(
  () => import('shared/modules/super-card/SuperCardPageDesktop'),
  'SuperCardPageDesktop',
);

const carrierAppRoutes: RouteObject[] = [
  ...(SuperCardExperimentPage
    ? [
        {
          path: 'super-card-experiment',
          element: (
            <Suspense fallback={<ContentProgressIndicator />}>
              <SuperCardExperimentPage />
            </Suspense>
          ),
        },
      ]
    : []),
  ...loadsRoutes,
  ...reportsRoutes,
  ...settingsRoutes,
  ...tripsRoutes,
  ...profileRoutes,
  ...contactsRoutes,
  ...dashboardRoutes,
  ...homeRoutes,
  ...usersPageRoutes,
  ...vehiclesPageRoutes,
  ...claimsRoutes,
  ...onboardingPacketsRoutes,
  { path: 'offers/:offerGUID?', element: <OffersPage /> },
  { path: 'suggested-loads-screen', element: <LoadSuggestionsPageMobile /> },
  { path: 'load-offers', element: <LoadOffersPageMobile /> },
  { path: 'tracking', element: <TrackingContainerPage /> },
  { path: 'loads-split/:loadGUID', element: <SplitLoadsPage /> },
  {
    path: 'ratings',
    element: <Navigate to={`/profile/overview${window.location.search}`} />,
  },
  { path: 'dispatchers/*', element: <Navigate to="/users" /> },
  { path: 'super-card/:section?', element: <SuperCardPageDesktop /> },
  { path: '*', element: <NotFoundRedirectPage /> },
];

export function NotFoundRedirectPage() {
  const features = useCarrierFeatures();

  if (!features) {
    return <ContentProgressIndicator />;
  }

  return (
    <Navigate
      to={isFlagEnabled(features, 'ctms_homepage') ? '/home' : '/loads'}
    />
  );
}

function SplitOrderRedirectPage() {
  const params = useParams<{ id: string }>();
  const { search } = useLocation();

  return <Navigate to={`/loads-split/${params.id}${search}`} />;
}

function ViewOffersRedirectPage() {
  const params = useParams<{ key: string }>();
  const { search } = useLocation();

  return <Navigate to={`/public-offer/${params.key}${search}`} />;
}

function RootOutlet() {
  return (
    <Suspense fallback={<ContentProgressIndicator />}>
      <Outlet />
    </Suspense>
  );
}

export const router = createBrowserRouter(
  [
    {
      element: <RootOutlet />,
      children: [
        ...loginRoutes,
        ...lockAccountRoutes,
        { path: '/orders/:id/split', element: <SplitOrderRedirectPage /> },
        { path: '/offers/:key/view/*', element: <ViewOffersRedirectPage /> },
        {
          path: '/public-offer/:guid',
          element: (
            <PublicOfferPageContextProvider>
              <PublicOfferPage />
            </PublicOfferPageContextProvider>
          ),
        },
        {
          path: '/driver-invite/:invitationCode',
          element: <DriverInvitationPage />,
        },
        {
          path: '/driver-activation/:activationCode',
          element: <DriverActivationPage />,
        },
        {
          path: '/driver-account-deletion/:token',
          element: <DriverAccountDeletionLayout />,
          children: [
            { path: 'questionnaire', element: <QuestionnaireStepPage /> },
            { path: 'confirmation', element: <ConfirmationStepPage /> },
            { path: 'completion', element: <CompletionStepPage /> },
            { index: true, element: <MainStepPage /> },
          ],
        },
        {
          path: '/user-invite/:invitationCode',
          element: <UserInvitationPage />,
        },
        { path: '/driver-login', element: <DriverAccountContainerPage /> },
        {
          path: '/driver-account',
          children: [
            {
              index: true,
              element: (
                <DriverAccountWrapper>
                  <DriverAccountPage />
                </DriverAccountWrapper>
              ),
            },
            {
              path: 'verify',
              element: (
                <VerifyAccountContainer>
                  <DriverVerifyAccountPage />
                </VerifyAccountContainer>
              ),
            },
            {
              path: 'delete',
              children: [
                {
                  index: true,
                  element: (
                    <DriverAccountWrapper>
                      <DriverDeleteAccountPage />
                    </DriverAccountWrapper>
                  ),
                },
                {
                  path: 'completion',
                  element: <DriverDeleteAccountCompletionPage />,
                },
                {
                  path: 'confirmation',
                  element: (
                    <DriverAccountWrapper>
                      <DriverDeleteAccountConfirmationPage />
                    </DriverAccountWrapper>
                  ),
                },
              ],
            },
          ],
        },
        {
          path: '/onboarding/:page?',
          children: [
            { path: 'invite-team', element: <OnboardingUserInviteStepPage /> },
            {
              path: 'questions-step-3',
              element: <OnboardingQuestionaireStep3Page />,
            },
            {
              path: 'questions-step-2',
              element: <OnboardingQuestionaireStep2Page />,
            },
            {
              path: 'questions-step-1',
              element: <OnboardingQuestionaireStep1Page />,
            },
            { index: true, element: <OnboardingWelcomeStepPage /> },
          ],
        },
        { path: '/trial-expired', element: <TrialExpiredPage /> },
        { path: '/go/supercard/:tab', element: <GoRedirectPage /> },

        {
          element: (
            <ServerErrorBoundary>
              <OnboardingWrapper>
                <CarrierOutlet />
              </OnboardingWrapper>
            </ServerErrorBoundary>
          ),
          children: carrierAppRoutes,
        },
      ],
    },
  ],

  { basename: basePath },
);
